/* eslint-disable @typescript-eslint/no-non-null-assertion */
// To resolve mini-css ordering
/* eslint-disable sort-imports */
import React, {
    useContext, useState,
} from 'react'
import Sidebar from './Sidebar'
import ChannelInviteModal from './Modal/ChannelInviteModal'
import TransferChatModal from './Modal/TransferChatModal'
import EndConversationModal from './Modal/EndConversationModal'
import CloseConversationModal from './Modal/CloseConversationModal'
import ConfirmationModal from './Modal/ConfirmationModal'
import EditCannedMessageModal from './Modal/EditCannedMessageModal'
import IdentityModal from './Modal/IdentityModal'
import Menubar from './Menubar'
import PromiseModal from './Modal/PromiseModal'
import SideMenu from './SideMenu'
import {isPropsObjectEqual} from '../utils/props'
import {styled} from '../stitches/config'
import {useAppSelector} from '../hooks'
import {Identity} from 'api/types/properties'
import {IdentityModalTypes} from 'actions/modal/types'
import {PromiseModalContext} from './Modal/PromiseModal/PromiseModalContext'
import {
    RouteParams,
} from 'navigation/types'
import {EmbedContext} from './EmbedContext'

const Jitsi = React.lazy(() => import('./Jitsi'))

interface MainProps {
    childName: string
    children : React.ReactElement | React.ReactElement[];
    hidden?: boolean
    routeParams: RouteParams
}

const sideMenuComponents = ['ChannelSettings', 'Home', 'QueueSettings', 'QueueStatistics', 'Realm', 'ScheduledAppointments', 'Settings', 'PermissionDenied']
const menuBarComponents = ['ChannelSettings', 'Home', 'QueueSettings', 'QueueStatistics', 'Realm', 'ScheduledAppointments', 'Settings', 'PermissionDenied']

const StyledContainer = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    variant: {hidden: {true: {display: 'none'}}},
})

const StyledMain = styled('div', {
    backgroundColor: '$mainBackground',
    display: 'flex',
    flexGrow: 1,
    height: '100vh',
    overflowY: 'auto',
    position: 'relative',
})

const StyledComponentContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden',
    width: '100%',
})

const StyledComponentContent = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    overflowY: 'auto',
})

const StyledChildrenWrapper = styled('div', {
    '> div': {
        borderLeft: '1px solid $menubarBorder',
        display: 'flex',
        minHeight: '100%',
    },
    'minHeight': '100%',
    'overflow': 'auto',
    'width': '100%',
})

const Main: React.FC<MainProps> = (props: MainProps): React.ReactElement => {
    const {
        childName: name,
        routeParams,
        children,
        hidden,
    } = props

    const {
        isJitsiActive,
        activeVideoChannelId,
    } = useAppSelector(({jitsi}) => ({
        activeVideoChannelId: jitsi.activeVideoChannelId,
        isJitsiActive: jitsi?.isActive ?? false,
    }), isPropsObjectEqual)

    const {
        channelInviteModal, closeConversation, confirmationModal,
        transferConversation, editCannedMessage, endConversation, identityModal,
    } = useAppSelector(({modal}) => ({
        channelInviteModal: modal.channelInviteModal,
        closeConversation: modal.closeConversation,
        confirmationModal: modal.confirmationModal,
        editCannedMessage: modal.editCannedMessage,
        endConversation: modal.endConversation,
        identityModal: modal.identityModal,
        transferConversation: modal.transferConversation,
    }), isPropsObjectEqual)

    const embed = useContext(EmbedContext)

    const hasSideMenu = sideMenuComponents.includes(name)
    const hasMenuBar = menuBarComponents.includes(name)

    const [promiseModalStack, setPromiseModalStack] = useState({})

    const promiseModalValue = React.useMemo(() => ({
        promiseModalStack,
        setPromiseModalStack,
    }), [promiseModalStack])

    const hideSidebar = embed?.variant === 'app' && embed?.hideSidebar

    return (
        <StyledContainer hidden={hidden}>
            <PromiseModalContext.Provider value={promiseModalValue}>
                {hideSidebar ? null : <Sidebar />}
                <StyledMain>
                    {!!isJitsiActive &&
                    <Jitsi channel_id={activeVideoChannelId as string} />}
                    <StyledComponentContainer>
                        { !!hasMenuBar &&
                        <Menubar name={name} routeParams={routeParams} />}
                        <StyledComponentContent>
                            { !!hasSideMenu &&
                            <SideMenu routeParams={routeParams} view={name} />}
                            {
                                hasSideMenu
                                    ? (
                                        <StyledChildrenWrapper>
                                            <div>
                                                {children}
                                            </div>
                                        </StyledChildrenWrapper>
                                    )
                                    : children
                            }
                        </StyledComponentContent>
                    </StyledComponentContainer>
                </StyledMain>
                {Boolean(channelInviteModal) &&
                <ChannelInviteModal
                    accessKey={channelInviteModal!.accessKey!}
                    noRedirectOnCancel={channelInviteModal?.noRedirectOnCancel}
                />}
                {Boolean(transferConversation) &&
                <TransferChatModal
                    channelId={transferConversation!.channelId!}
                    queueId={transferConversation!.queueId!}
                />}

                {Boolean(endConversation) &&
                <EndConversationModal
                    channelId={endConversation!.channelId!}
                />}

                {Boolean(closeConversation) &&
                <CloseConversationModal
                    channelId={closeConversation!.channelId!}
                />}

                {Boolean(confirmationModal) &&
                <ConfirmationModal
                    onConfirm={confirmationModal!.onConfirm!}
                    text={confirmationModal!.text!}
                    title={confirmationModal!.title!}
                />}

                {Boolean(editCannedMessage) &&
                <EditCannedMessageModal
                    keyword={editCannedMessage!.keyword!}
                    message={editCannedMessage!.message!}
                    organization={editCannedMessage!.organization!}
                    realmId={editCannedMessage!.realmId!}
                />}
                {!!identityModal &&
                <IdentityModal
                    identity={identityModal.identity as Identity}
                    type={identityModal.identityType as IdentityModalTypes}
                />}
                <PromiseModal />
            </PromiseModalContext.Provider>
        </StyledContainer>
    )
}

export default Main

Main.defaultProps = {hidden: false}
